<script setup>
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import LocaleSwitcher from '@/components/layout/LocaleSwitcher.vue';
import { navigateToHomeSection } from '@/composables/useHomeSectionNav.js';
import { BRAND_LOGO_SRC } from '@/config/brand.js';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const openContact = inject('openContact', () => {});
const pageLoading = inject('pageLoading', ref(false));
const pageRevealing = inject('pageRevealing', ref(false));
const menuOpen = ref(false);
const scrolled = ref(false);
const appUrl = import.meta.env.VITE_APP_URL || 'http://localhost:5176';

const hasHeroHeader = computed(() =>
  route.name === 'home' || route.name === 'about' || route.name === 'cases' || route.name === 'updates',
);
const isTransparent = computed(() => hasHeroHeader.value && !scrolled.value && !menuOpen.value);

/** 顶栏实体背景：滚动后，或菜单打开时与下拉菜单同色 */
const headerSurfaceClass = computed(() => {
  if (menuOpen.value) {
    return 'border-border/60 bg-background/98 backdrop-blur-xl';
  }
  if (isTransparent.value) {
    return 'border-transparent bg-transparent backdrop-blur-none';
  }
  if (scrolled.value) {
    return 'border-border/60 bg-background/80 backdrop-blur-xl';
  }
  return 'border-transparent bg-background/60 backdrop-blur-md';
});


const navItems = computed(() => [
  { id: 'home', label: t('nav.home'), path: '/' },
  { id: 'product', label: t('nav.product'), path: '/', hash: '#product' },
  { id: 'cases', label: t('nav.cases'), path: '/cases' },
  { id: 'updates', label: t('nav.updates'), path: '/updates' },
  { id: 'about', label: t('nav.about'), path: '/about' },
]);

/** 首页区块高亮：产品能力（已整合工作流程） */
const homeActiveSection = ref('hero');

function getScrollOffset() {
  const padding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop);
  return Number.isFinite(padding) ? padding : 80;
}

function updateHomeActiveSection() {
  if (route.name !== 'home') return;

  const viewportTop = getScrollOffset();
  const viewportBottom = window.innerHeight;
  let bestId = 'hero';
  let bestVisible = -1;

  for (const id of ['hero', 'product']) {
    const el = document.getElementById(id);
    if (!el) continue;

    const { top, bottom } = el.getBoundingClientRect();
    const visibleTop = Math.max(top, viewportTop);
    const visibleBottom = Math.min(bottom, viewportBottom);
    const visibleHeight = Math.max(0, visibleBottom - visibleTop);

    if (visibleHeight >= bestVisible) {
      bestVisible = visibleHeight;
      bestId = id;
    }
  }

  homeActiveSection.value = bestId;
}

function isNavItemActive(item) {
  if (item.path === '/cases') return route.name === 'cases';
  if (item.path === '/updates') return route.name === 'updates';
  if (item.path === '/about') return route.name === 'about';
  if (route.name !== 'home') return false;

  if (item.id === 'home') return homeActiveSection.value === 'hero';
  if (item.id === 'product') return homeActiveSection.value === 'product';

  return false;
}

function desktopNavClass(item) {
  const active = isNavItemActive(item);
  const base = active ? 'is-active' : '';

  if (isTransparent.value) {
    return [
      base,
      active ? 'text-white' : 'text-white/70 hover:text-white',
      'nav-link--light',
    ].join(' ');
  }

  return [
    base,
    active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
  ].join(' ');
}

function mobileNavClass(item) {
  return isNavItemActive(item)
    ? 'text-primary'
    : 'text-foreground/80 hover:text-foreground';
}

async function navigateNav(item) {
  closeMenu();
  if (item.hash) {
    await navigateToHomeSection(router, route, item.hash);
    if (item.id === 'product') homeActiveSection.value = 'product';
    if (item.id === 'journey') homeActiveSection.value = 'journey';
    return;
  }
  if (item.path === '/') {
    homeActiveSection.value = 'hero';
  }
  await router.push(item.path);
}

function onScroll() {
  scrolled.value = window.scrollY > 12;
  updateHomeActiveSection();
}

function closeMenu() {
  menuOpen.value = false;
}

watch(menuOpen, (value) => {
  document.body.style.overflow = value ? 'hidden' : '';
});

watch(() => route.fullPath, () => {
  closeMenu();
  onScroll();
  updateHomeActiveSection();
});

onMounted(() => {
  onScroll();
  updateHomeActiveSection();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('hashchange', updateHomeActiveSection);
});

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll);
  window.removeEventListener('hashchange', updateHomeActiveSection);
  document.body.style.overflow = '';
});
</script>

<template>
  <header
    class="sticky top-0 z-50 border-b transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300 ease-in-out"
    :class="headerSurfaceClass"
  >
    <div
      class="header-inner flex h-16 items-center justify-between gap-6 lg:h-[4.25rem]"
      :class="{ 'header-inner--transparent': isTransparent }"
    >
      <!-- 品牌 -->
      <RouterLink to="/" class="group flex min-w-0 items-center gap-2.5" @click="closeMenu">
        <span
          data-header-logo
          class="size-8 shrink-0 overflow-hidden transition-[opacity,box-shadow] duration-300 ease-in-out sm:size-9"
          :class="{ 'opacity-0': pageLoading && !pageRevealing }"
        >
          <img :src="BRAND_LOGO_SRC" :alt="t('brand.name')" class="size-full object-contain" />
        </span>
        <span
          class="truncate text-[15px] font-semibold tracking-tight transition-colors duration-300 ease-in-out sm:text-base"
          :class="isTransparent ? 'text-white' : 'text-foreground'"
        >
          {{ t('brand.name') }}
        </span>
      </RouterLink>

      <!-- 桌面导航：纯文字 + 底线指示 -->
      <nav class="absolute left-1/2 hidden -translate-x-1/2 items-center gap-1 xl:flex" :aria-label="t('nav.mainLabel')">
        <a
          v-for="item in navItems"
          :key="item.id"
          href="#"
          class="nav-link relative px-3.5 py-2 text-[13px] font-medium tracking-wide transition-colors duration-300 ease-in-out"
          :class="desktopNavClass(item)"
          :aria-current="isNavItemActive(item) ? 'page' : undefined"
          @click.prevent="navigateNav(item)"
        >
          {{ item.label }}
        </a>
      </nav>

      <!-- 右侧操作 -->
      <div class="flex shrink-0 items-center gap-0.5 sm:gap-2">
        <LocaleSwitcher :variant="isTransparent ? 'transparent' : 'default'" />

        <!-- <a
          :href="appUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="hidden px-3 py-2 text-[13px] font-medium transition-colors duration-300 ease-in-out lg:inline-block"
          :class="isTransparent ? 'text-white/70 hover:text-white' : 'text-muted-foreground hover:text-foreground'"
        >
          {{ t('nav.login') }}
        </a> -->

        <button
          type="button"
          class="hidden h-9 items-center px-4 text-[13px] font-semibold transition-[transform,background-color,box-shadow] duration-300 ease-in-out hover:-translate-y-0.5 sm:inline-flex"
          :class="
            isTransparent
              ? 'rounded-md bg-white text-slate-900 hover:bg-white/90'
              : 'rounded-md bg-primary text-primary-foreground hover:bg-blue-700'
          "
          @click="openContact()"
        >
          {{ t('nav.contact') }}
        </button>

        <button
          type="button"
          class="grid size-9 place-items-center transition-[color,background-color,border-color] duration-300 ease-in-out xl:hidden"
          :class="isTransparent ? 'text-white/80 hover:text-white' : 'text-muted-foreground hover:text-foreground'"
          :aria-label="t('nav.menu')"
          :aria-expanded="menuOpen"
          @click="menuOpen = !menuOpen"
        >
          <svg v-if="!menuOpen" viewBox="0 0 24 24" class="size-5" fill="none" stroke="currentColor" stroke-width="1.75">
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
          <svg v-else viewBox="0 0 24 24" class="size-5" fill="none" stroke="currentColor" stroke-width="1.75">
            <path d="m6 6 12 12M18 6 6 18" />
          </svg>
        </button>
      </div>
    </div>

    <!-- 移动端菜单：扁平列表 -->
    <Transition name="menu">
      <div
        v-if="menuOpen"
        class="absolute inset-x-0 top-full border-t border-border/60 bg-background/98 backdrop-blur-xl xl:hidden"
      >
        <nav class="header-inner flex flex-col py-4" :aria-label="t('nav.mobileLabel')">
          <a
            v-for="item in navItems"
            :key="item.id"
            href="#"
            class="border-b border-border/40 px-1 py-3.5 text-[15px] font-medium transition-colors last:border-b-0"
            :class="mobileNavClass(item)"
            :aria-current="isNavItemActive(item) ? 'page' : undefined"
            @click.prevent="navigateNav(item)"
          >
            {{ item.label }}
          </a>

          <div class="mt-5 flex items-center gap-4 px-1">
            <!-- <a
              :href="appUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm font-medium text-muted-foreground hover:text-foreground"
              @click="closeMenu"
            >
              {{ t('nav.login') }}
            </a> -->
            <button
              type="button"
              class="ml-auto h-10 rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground"
              @click="openContact(); closeMenu()"
            >
              {{ t('nav.contact') }}
            </button>
          </div>
        </nav>
      </div>
    </Transition>
  </header>
</template>

<style scoped>
.header-inner {
  margin-inline: auto;
  width: 100%;
  max-width: 80rem;
  padding-inline: 1.5rem;
  transition:
    max-width 300ms ease-in-out,
    padding-inline 300ms ease-in-out;
}

.header-inner--transparent {
  max-width: 100%;
  padding-inline: 1.25rem;
}

@media (min-width: 640px) {
  .header-inner--transparent {
    padding-inline: 2rem;
  }
}

@media (min-width: 1024px) {
  .header-inner--transparent {
    padding-inline: 2.5rem;
  }
}

@media (min-width: 1280px) {
  .header-inner--transparent {
    padding-inline: 3rem;
  }
}

/* 导航底线：默认隐藏，悬停/激活展开 */
.nav-link::after {
  content: '';
  position: absolute;
  left: 0.875rem;
  right: 0.875rem;
  bottom: 0.25rem;
  height: 1.5px;
  border-radius: 1px;
  background: var(--primary);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 300ms ease-in-out;
}

.nav-link--light::after {
  background: currentColor;
}

.nav-link:hover::after,
.nav-link.is-active::after {
  transform: scaleX(1);
}

@media (prefers-reduced-motion: reduce) {
  .header-inner {
    transition: none;
  }

  .nav-link::after {
    transition: none;
  }
}

.menu-enter-active,
.menu-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.menu-enter-from,
.menu-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem);
}
</style>
